import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { Copy, Loader2, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useUpdateCampaignSourceFields } from '@/api/campaigns/use-campaigns';
import type { Campaign, SourceField } from '@/api/campaigns/types';
import { buildCurlExample } from '@/components/campaigns/landingPage/curlExample';

export interface FieldsStepProps {
    campaign: Campaign;
    onRefetchCampaign: () => void;
    onBack: () => void;
    onAdvance: () => void;
}

/** A blank manual row, given a unique-enough default key so the user can rename it immediately. */
function blankRow(existing: SourceField[]): SourceField {
    let n = existing.length + 1;
    while (existing.some(f => f.key === `field_${n}`)) n += 1;
    return { key: `field_${n}`, label: '', type: 'string', sample: '' };
}

const FieldsStep: React.FC<FieldsStepProps> = ({
    campaign,
    onRefetchCampaign,
    onBack,
    onAdvance,
}) => {
    const { t } = useTranslation('crm');
    const { enqueueSnackbar } = useSnackbar();
    const [activeTab, setActiveTab] = useState<'capture' | 'manual'>(
        campaign.source_fields.length > 0 ? 'manual' : 'capture'
    );
    const [rows, setRows] = useState<SourceField[]>(campaign.source_fields);

    const updateSourceFields = useUpdateCampaignSourceFields();
    const isLandingPage = campaign.source === 'landing_page';
    const hasFields = campaign.source_fields.length > 0;

    // Keep the manual editor in sync when a capture poll brings in new keys.
    useEffect(() => {
        setRows(campaign.source_fields);
    }, [campaign.source_fields]);

    // Poll GET /campaigns/{id} every 3s while the capture panel is open and
    // nothing has arrived yet (field-mapping.md §7 / TASKS.md contract).
    useEffect(() => {
        if (!isLandingPage || activeTab !== 'capture' || hasFields) return undefined;
        const interval = setInterval(() => onRefetchCampaign(), 3000);
        return () => clearInterval(interval);
    }, [isLandingPage, activeTab, hasFields, onRefetchCampaign]);

    const curlExample = useMemo(
        () => buildCurlExample(campaign.endpoint_url ?? '', campaign.source_fields),
        [campaign.endpoint_url, campaign.source_fields]
    );

    const copy = async (text: string, successKey: string) => {
        try {
            await navigator.clipboard.writeText(text);
            enqueueSnackbar(t(successKey), { variant: 'success' });
        } catch {
            enqueueSnackbar(t('campaigns.editor.fields.copyFailed'), { variant: 'error' });
        }
    };

    const updateRow = (index: number, patch: Partial<SourceField>) => {
        setRows(prev => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    };

    const removeRow = (index: number) => {
        setRows(prev => prev.filter((_, i) => i !== index));
    };

    const addRow = () => {
        setRows(prev => [...prev, blankRow(prev)]);
    };

    const saveManualFields = async () => {
        const cleaned = rows
            .map(row => ({
                ...row,
                key: row.key.trim(),
                label: row.label?.trim() || row.key.trim(),
            }))
            .filter(row => row.key.length > 0);

        await updateSourceFields.mutateAsync({ id: campaign.id, data: { source_fields: cleaned } });
        enqueueSnackbar(t('campaigns.editor.fields.saved'), { variant: 'success' });
    };

    const handleContinue = async () => {
        try {
            if (isLandingPage && activeTab === 'manual') {
                await saveManualFields();
            }
            onAdvance();
        } catch {
            enqueueSnackbar(t('campaigns.editor.fields.saveFailed'), { variant: 'error' });
        }
    };

    if (!isLandingPage) {
        return (
            <div className="space-y-6">
                <Card>
                    <CardHeader>
                        <CardTitle>{t('campaigns.editor.fields.facebookTitle')}</CardTitle>
                        <p className="text-sm text-muted-foreground">
                            {t('campaigns.editor.fields.facebookDescription')}
                        </p>
                    </CardHeader>
                    <CardContent>
                        {campaign.source_fields.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                                {t('campaigns.editor.fields.facebookNoFields')}
                            </p>
                        ) : (
                            <div className="divide-y rounded-lg border">
                                {campaign.source_fields.map(field => (
                                    <div
                                        key={field.key}
                                        className="flex items-center justify-between p-3"
                                    >
                                        <div>
                                            <p className="text-sm font-medium">{field.label}</p>
                                            <p className="text-xs text-muted-foreground">
                                                {field.key}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {field.options && field.options.length > 0 && (
                                                <Badge variant="outline">
                                                    {t('campaigns.editor.fields.optionsCount', {
                                                        count: field.options.length,
                                                    })}
                                                </Badge>
                                            )}
                                            <Badge variant="secondary">{field.type}</Badge>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                <div className="flex items-center justify-between border-t pt-4">
                    <Button variant="outline" onClick={onBack}>
                        {t('campaigns.editor.actions.back')}
                    </Button>
                    <Button onClick={onAdvance} disabled={campaign.source_fields.length === 0}>
                        {t('campaigns.editor.actions.continue')}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <Tabs
                value={activeTab}
                onValueChange={value => setActiveTab(value as 'capture' | 'manual')}
            >
                <TabsList>
                    <TabsTrigger value="capture">
                        {t('campaigns.editor.fields.captureTab')}
                    </TabsTrigger>
                    <TabsTrigger value="manual">
                        {t('campaigns.editor.fields.manualTab')}
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="capture" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>{t('campaigns.editor.fields.endpointTitle')}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {campaign.endpoint_url ? (
                                <>
                                    <div className="flex items-center gap-2">
                                        <Input
                                            readOnly
                                            value={campaign.endpoint_url}
                                            className="font-mono text-xs"
                                        />
                                        <Button
                                            variant="outline"
                                            size="icon-sm"
                                            onClick={() =>
                                                copy(
                                                    campaign.endpoint_url!,
                                                    'campaigns.editor.fields.urlCopied'
                                                )
                                            }
                                            aria-label={t('campaigns.editor.fields.copyUrl')}
                                        >
                                            <Copy className="h-4 w-4" />
                                        </Button>
                                    </div>

                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <Label>
                                                {t('campaigns.editor.fields.curlExample')}
                                            </Label>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() =>
                                                    copy(
                                                        curlExample,
                                                        'campaigns.editor.fields.curlCopied'
                                                    )
                                                }
                                            >
                                                <Copy className="h-3.5 w-3.5 mr-1" />
                                                {t('campaigns.editor.fields.copyCurl')}
                                            </Button>
                                        </div>
                                        <pre className="overflow-x-auto rounded-md bg-muted p-3 text-xs">
                                            {curlExample}
                                        </pre>
                                    </div>
                                </>
                            ) : (
                                <p className="text-sm text-muted-foreground">
                                    {t('campaigns.editor.fields.noEndpointPermission')}
                                </p>
                            )}

                            <div className="rounded-lg border bg-muted/30 p-4">
                                {hasFields ? (
                                    <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
                                        <RefreshCw className="h-4 w-4" />
                                        {t('campaigns.editor.fields.fieldsDetected', {
                                            count: campaign.source_fields.length,
                                        })}
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        {t('campaigns.editor.fields.waitingForSubmission')}
                                    </div>
                                )}
                            </div>

                            {hasFields && (
                                <div className="divide-y rounded-lg border">
                                    {campaign.source_fields.map(field => (
                                        <div
                                            key={field.key}
                                            className="flex items-center justify-between p-3 text-sm"
                                        >
                                            <span className="font-mono text-xs text-muted-foreground">
                                                {field.key}
                                            </span>
                                            <span className="truncate pl-4 text-right">
                                                {field.sample !== undefined && field.sample !== null
                                                    ? String(field.sample)
                                                    : '—'}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="manual" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>{t('campaigns.editor.fields.manualTitle')}</CardTitle>
                            <p className="text-sm text-muted-foreground">
                                {t('campaigns.editor.fields.manualDescription')}
                            </p>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 text-xs font-medium text-muted-foreground">
                                <span>{t('campaigns.editor.fields.colKey')}</span>
                                <span>{t('campaigns.editor.fields.colLabel')}</span>
                                <span>{t('campaigns.editor.fields.colSample')}</span>
                                <span />
                            </div>
                            {rows.map((row, index) => (
                                <div
                                    key={index}
                                    className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2"
                                >
                                    <Input
                                        value={row.key}
                                        onChange={e => updateRow(index, { key: e.target.value })}
                                        placeholder="e.g. contact.phone"
                                    />
                                    <Input
                                        value={row.label ?? ''}
                                        onChange={e => updateRow(index, { label: e.target.value })}
                                        placeholder={t('campaigns.editor.fields.colLabel')}
                                    />
                                    <Input
                                        value={
                                            row.sample !== undefined && row.sample !== null
                                                ? String(row.sample)
                                                : ''
                                        }
                                        onChange={e => updateRow(index, { sample: e.target.value })}
                                        placeholder={t('campaigns.editor.fields.colSample')}
                                    />
                                    <Button
                                        variant="ghost"
                                        size="icon-sm"
                                        onClick={() => removeRow(index)}
                                        aria-label={t('actions.delete')}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            ))}
                            <div className="flex items-center justify-between pt-2">
                                <Button variant="outline" size="sm" onClick={addRow}>
                                    <Plus className="h-4 w-4 mr-1" />
                                    {t('campaigns.editor.fields.addField')}
                                </Button>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={saveManualFields}
                                    disabled={updateSourceFields.isPending}
                                >
                                    {updateSourceFields.isPending && (
                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                    )}
                                    {t('campaigns.editor.fields.saveFields')}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            <div className="flex items-center justify-between border-t pt-4">
                <Button variant="outline" onClick={onBack}>
                    {t('campaigns.editor.actions.back')}
                </Button>
                <Button
                    onClick={handleContinue}
                    disabled={
                        (activeTab === 'capture' && !hasFields) ||
                        (activeTab === 'manual' && rows.filter(r => r.key.trim()).length === 0) ||
                        updateSourceFields.isPending
                    }
                >
                    {updateSourceFields.isPending && (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    )}
                    {t('campaigns.editor.actions.continue')}
                </Button>
            </div>
        </div>
    );
};

export default FieldsStep;
